import { LanguageSwitcher, LoadingSpinner, ThemeToggle } from '@/components';
import { BranchTokenValidationGuard } from '@/components/BranchTokenValidationGuard';
import { ThemeProvider } from '@/components/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { lazy, Suspense } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

// Lazy-loaded page components for code splitting
const AccountFormPage = lazy(() => import('@/pages/AccountFormPage'));
const AdminKycFormPage = lazy(() => import('@/pages/AdminKycFormPage'));
const DocumentUploadPage = lazy(() => import('@/pages/DocumentUploadPage'));
const KycFormPage = lazy(() => import('@/pages/KycFormPage'));
const BranchesPage = lazy(() =>
  import('@/pages/branches').then(module => ({ default: module.BranchesPage }))
);

/** Query client configuration for TanStack Query */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * SettingsControls - Language and theme toggle controls
 * Fixed positioning in the top-right corner of the viewport
 */
const SettingsControls: React.FC = () => (
  <div className='absolute top-4 right-4 flex items-center gap-2'>
    <LanguageSwitcher />
    <ThemeToggle />
  </div>
);

interface PageLayoutProps {
  children: React.ReactNode;
}

/**
 * PageLayout - Common layout wrapper for all form pages
 * Includes settings controls (language/theme) and renders page content
 */
const PageLayout: React.FC<PageLayoutProps> = ({ children }) => (
  <>
    <SettingsControls />
    {children}
  </>
);

const AppRoutes: React.FC = () => (
  <Routes>
    <Route
      path='/form/account/:token'
      element={
        <PageLayout>
          <AccountFormPage />
        </PageLayout>
      }
    />
    <Route
      path='/form/kyc/:token'
      element={
        <PageLayout>
          <KycFormPage />
        </PageLayout>
      }
    />
    <Route
      path='/admin-edit/kyc/:token'
      element={
        <PageLayout>
          <AdminKycFormPage />
        </PageLayout>
      }
    />
    <Route
      path='/form/documents/:token'
      element={
        <PageLayout>
          <DocumentUploadPage />
        </PageLayout>
      }
    />
    <Route
      path='/form/locations/:token'
      element={
        <PageLayout>
          <BranchTokenValidationGuard>
            <BranchesPage />
          </BranchTokenValidationGuard>
        </PageLayout>
      }
    />
  </Routes>
);

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute='class'
      defaultTheme='system'
      enableSystem
      disableTransitionOnChange={false}
      storageKey='nova-user-theme'
    >
      <Router>
        <div className='min-h-screen bg-background text-foreground'>
          <Suspense
            fallback={
              <div className='min-h-screen flex items-center justify-center'>
                <LoadingSpinner size='xl' />
              </div>
            }
          >
            <AppRoutes />
          </Suspense>
          <Toaster />
        </div>
      </Router>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
